import {IvrAction} from "@/api";
import {MutationFunction} from "@/utils/Form";
import {ExtensionProfileExport} from "../export/types";
import {defineMessage, MessageDescriptor} from "react-intl";

export interface Stage {
    id: string;
    label: MessageDescriptor;
}

// Tracking the progress of the mutations
export const stages = [
    {
        id: "create_domain",
        label: defineMessage({id: "domain_import.stages.create_domain", defaultMessage: "Create domain"}),
    },
    {
        id: "update_domain",
        label: defineMessage({id: "domain_import.stages.update_domain", defaultMessage: "Update domain"}),
    },
    {
        id: "create_locations",
        label: defineMessage({id: "domain_import.stages.create_locations", defaultMessage: "Create locations"}),
    },
    {
        id: "create_extensions",
        label: defineMessage({id: "domain_import.stages.create_extensions", defaultMessage: "Create users"}),
    },
    {
        id: "create_ivrs",
        label: defineMessage({id: "domain_import.stages.create_ivrs", defaultMessage: "Create auto attendants"}),
    },
    {
        id: "update_ivr_profiles",
        label: defineMessage({
            id: "domain_import.stages.update_ivr_profiles",
            defaultMessage: "Update auto attendants",
        }),
    },
    {
        id: "create_faxes",
        label: defineMessage({id: "domain_import.stages.create_faxes", defaultMessage: "Create faxes"}),
    },
    {
        id: "create_gateways",
        label: defineMessage({id: "domain_import.stages.create_gateways", defaultMessage: "Create gateways"}),
    },
    {
        id: "create_gateway_routing",
        label: defineMessage({
            id: "domain_import.stages.create_gateway_routing",
            defaultMessage: "Create gateway routing",
        }),
    },
    {
        id: "create_groups",
        label: defineMessage({id: "domain_import.stages.create_groups", defaultMessage: "Create groups"}),
    },
    {
        id: "add_domain_admins",
        label: defineMessage({id: "domain_import.stages.add_domain_admins", defaultMessage: "Add domain admins"}),
    },
    {
        id: "domain_address_book",
        label: defineMessage({
            id: "domain_import.stages.domain_address_book",
            defaultMessage: "Importing domain address book",
        }),
    },
] as const satisfies readonly Stage[];

export type StageID = (typeof stages)[number]["id"];

export const steps = {
    creating_domain: defineMessage({id: "domain_import.steps.creating_domain", defaultMessage: "Creating domain"}),
    updating_domain_allocations: defineMessage({
        id: "domain_import.steps.updating_domain_allocations",
        defaultMessage: "Updating domain allocations",
    }),
    updating_domain_settings: defineMessage({
        id: "domain_import.steps.updating_domain_settings",
        defaultMessage: "Updating domain settings",
    }),
    creating_locations: defineMessage({
        id: "domain_import.steps.creating_locations",
        defaultMessage: "Creating locations",
    }),
    creating_extensions: defineMessage({
        id: "domain_import.steps.creating_extensions",
        defaultMessage: "Creating users",
    }),
    creating_extensions_devices: defineMessage({
        id: "domain_import.steps.creating_extensions_devices",
        defaultMessage: "Creating user devices",
    }),
    creating_ivrs: defineMessage({id: "domain_import.steps.creating_ivrs", defaultMessage: "Creating auto attendants"}),
    updating_ivr_profiles: defineMessage({
        id: "domain_import.steps.updating_ivr_profiles",
        defaultMessage: "Updating auto attendant profiles",
    }),
    creating_faxes: defineMessage({id: "domain_import.steps.creating_faxes", defaultMessage: "Creating faxes"}),
    creating_groups: defineMessage({id: "domain_import.steps.creating_groups", defaultMessage: "Creating groups"}),
    add_domain_admins: defineMessage({
        id: "domain_import.steps.add_domain_admins",
        defaultMessage: "Adding domain admins",
    }),
    importing_domain_address_book: defineMessage({
        id: "domain_import.steps.importing_domain_address_book",
        defaultMessage: "Importing domain address book",
    }),
    importing_domain_admins: defineMessage({
        id: "domain_import.steps.importing_domain_admins",
        defaultMessage: "Importing domain admins",
    }),
};

export type StepID = keyof typeof steps;

// These types are really only for helping with understanding the code
export type CreateMutation = () => Promise<number>;
export type UpdateMutation = (id: number) => Promise<void>;
export type SpecialDay = ExtensionProfileExport["specialDays"][number];
export type ActivationTime = ExtensionProfileExport["activationTimes"][number];
export type ProfileRule = ExtensionProfileExport["rules"][number];

// Gives back the type of the variables of the mutation
export type ExtractMutationVariables<T> = T extends MutationFunction<unknown, infer P> ? P : never;

const ivrButtonOrder = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 10, 11, 12] as const;
type IvrButtonNumber = (typeof ivrButtonOrder)[number];

export const sortIvrActions = (a: IvrAction, b: IvrAction): number => {
    return ivrButtonOrder.indexOf(a.button as IvrButtonNumber) - ivrButtonOrder.indexOf(b.button as IvrButtonNumber);
};
